import { useEffect, useState } from 'react';
import { interpretationsApi } from '../services/api';
import { Interpretation, InterpretationStatus } from '../types';

/**
 * The single seat of the server-side search (E06-F01, E06-F02).
 *
 * The search used to be a page sweep filtered in the browser: 100 rows a request, ten
 * requests, and a patient on page 13 reported as non-existent. Now the SERVER walks its
 * own light index and answers once, so this hook moves exactly one request per
 * `(term, status, company)` and knows nothing about how the match is computed -- no
 * lowercasing, no accent stripping: the server normalises the term with one rule and
 * compares the company by equality against the stored name.
 *
 * It serves BOTH controls. There are no longer two data paths: the search box and the
 * company filter are two of the three optional conditions of the same request, and
 * the sweep that used to feed the company filter is gone.
 */

export interface SearchState {
  /** `null` = no valid search (switched off, or it failed). `[]` = searched, found nothing. */
  results: Interpretation[] | null;
  searching: boolean;
  /** The server hit its result cap before exhausting the index. */
  truncated: boolean;
  failed: boolean;
  /** The server's cap, from the RESPONSE, so the screen prints the true number. */
  maxResults: number | null;
}

const OFF: SearchState = {
  results: null,
  searching: false,
  truncated: false,
  failed: false,
  maxResults: null,
};

/**
 * Searches the server while there is a term OR a company.
 *
 * @param term The debounced, trimmed search term. `''` is "no term".
 * @param status The list's status filter, sent as-is; `'all'` is the ABSENCE of the
 *               parameter, never the literal string.
 * @param company The chosen company, exactly as the dropdown's option value (the
 *                server's stored name); `null` is "no company". Sent as-is: the server
 *                compares it by equality, so any client-side normalisation would find
 *                nothing.
 *
 * ON when `term !== '' || Boolean(company)` -- the SAME criterion that decides whether
 * `company` travels, so the hook is never active with no parameters (that would be a
 * 400 the client manufactured). OFF otherwise: no request, state reset.
 *
 * Deps are `[term, status, company]`: each change is a new request, and the cleanup
 * marks the previous one `cancelled` so an answer that lands late cannot repaint over
 * a newer one. Only the keys that are present are sent -- no `q: ''`, no
 * `company: undefined` -- which is also what keeps F01's `toHaveBeenCalledWith({ q,
 * status })` assertions true without a company. A rejected request, or a response with
 * no `items` (which is what the Jest automock hands back), is a FAILURE: `results`
 * stays `null`, never `[]`.
 */
export function useInterpretationSearch(
  term: string,
  status: InterpretationStatus | 'all',
  company: string | null = null
): SearchState {
  const [state, setState] = useState<SearchState>(OFF);

  useEffect(() => {
    const active = term !== '' || Boolean(company);
    if (!active) {
      setState(OFF);
      return;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, searching: true, failed: false }));

    const run = async () => {
      try {
        const response = await interpretationsApi.search({
          ...(term ? { q: term } : {}),
          ...(status !== 'all' ? { status } : {}),
          ...(company ? { company } : {}),
        });
        if (!response || !Array.isArray(response.items)) {
          throw new Error('search response without items');
        }
        if (!cancelled) {
          setState({
            results: response.items,
            searching: false,
            truncated: Boolean(response.truncated),
            failed: false,
            maxResults: response.max_results ?? null,
          });
        }
      } catch (error) {
        console.error('Error searching interpretations:', error);
        // Reporting "no results" for a failed request would be the lie the old
        // page-scoped search told: say the search broke, not that nothing matched.
        if (!cancelled) {
          setState({ ...OFF, failed: true });
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [term, status, company]);

  return state;
}
